"use client";

import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'sonner';
import { useRouter } from "next/navigation";
import { useSession, useUser } from '@clerk/nextjs';
import { DataGrid, DataGridColumnHeader, TDataGridRequestParams, useDataGrid } from '../../common/data-grid';
import { ScreenLoader } from '../../common/loaders/ScreenLoader';
import { fetchUserMembershis } from '@/lib/action';
import { KeenIcon } from '../../common/keenicons';
import { formatIsoDate } from '@/app/utils/Date';
import { MembershipView } from './MembershipView';


const MembershipList = ({ total }: any) => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const { user, isLoaded: userLoaded } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const intl = useIntl();
  const [details, setDetails] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const handleViewModalClose = () => {
    setViewModalOpen(false);
    setDetails(null)
  };

  useEffect(() => {
    if (userLoaded && !user?.id) {
      router.push('/sign-in');
      toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
    }

    if (sessionLoaded && !session?.id) {
      router.push('/sign-in');
      toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
    }

  }, [userLoaded, sessionLoaded, user, session]);


  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorFn: (row) => row.title,
        id: 'id',
        header: ({ column }) => (
          <DataGridColumnHeader
            title={formatMessage({ id: 'ID', defaultMessage: 'ID' })}
            column={column}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        cell: (info) => (
          <span
            className="leading-none font-medium text-sm text-gray-900 hover:text-primary"
          >
            {info.row.original.data?.id}
          </span>
        ),
        meta: {
          headerTitle: formatMessage({ id: 'ID', defaultMessage: 'ID' })
        },
      },
      {
        accessorFn: (row) => row.title,
        id: 'title',
        enableSorting: false,
        enableHiding: false,
        header: ({ column }) => (
          <DataGridColumnHeader
            title={formatMessage({ id: 'NAME', defaultMessage: 'Name' })}
            column={column}
          />
        ),
        cell: (info) => (
          <div className='flex flex-col gap-1'>
            <span className="leading-none font-medium text-sm text-gray-900">
              {info.row.original.data?.membership?.title}
            </span>
            <span className='text-xs font-normal text-gray-700 leading-3'> {info.row.original.data?.membership?.description}</span>
          </div>
        ),
        meta: {
          headerTitle: formatMessage({ id: 'NAME', defaultMessage: 'Name' })
        },
      },
      {
        accessorFn: (row) => row.extension_builder,
        id: 'extension builder',
        header: ({ column }) => (
          <DataGridColumnHeader
            title={formatMessage({ id: "EXTENSION_BUILDER" })}
            column={column} />
        ),
        enableSorting: false,
        enableHiding: false,
        cell: (info) => (
            <div className='flex flex-col gap-1'>
              <p className=''>
                <span> <FormattedMessage id="EXTENSION_APPLICABLE" /></span>
                <span>{info.row.original.extension_info?.max}</span>
              </p>
              <p className=''>
                <span> <FormattedMessage id="CREATED_EXTENSIONS" /></span>
                <span>{info.row.original.extension_info?.current}</span>
              </p>
            </div>
          ),
        meta: {
          headerTitle: formatMessage({ id: "EXTENSION_BUILDER" }),
        }
      },
      {
        accessorFn: (row) => row.created_at,
        id: 'created_at',
        header: ({ column }) => (
          <DataGridColumnHeader
            title={formatMessage({ id: "CREATED_AT" })}
            column={column} />
        ),
        enableSorting: false,
        enableHiding: false,
        cell: (info) => formatIsoDate(info.row.original.data?.created_at),
        meta: {
          headerTitle: formatMessage({ id: "CREATED_AT" }),
        }
      },
      {
        accessorFn: (row) => row.expired_at,
        id: 'expired_at',
        header: ({ column }) => (
          <DataGridColumnHeader
            title={formatMessage({ id: "EXPIRED_AT" })}
            column={column} />
        ),
        enableSorting: false,
        enableHiding: false,
        cell: (info) => formatIsoDate(info.row.original.data?.expired_at),
        meta: {
          headerTitle: formatMessage({ id: "EXPIRED_AT" }),
        }
      },
      {
        id: 'view',
        header: () => '',
        enableSorting: false,
        enableHiding: false,
        cell: (info) => (
          <span
            className="btn btn-sm btn-icon btn-clear btn-light"
            onClick={async (e) => {
              setDetails(info.row.original);
              setViewModalOpen(true);
            }}
          >
            <KeenIcon icon="eye" />
          </span>
        ),
        meta: {
          headerClassName: 'w-[60px]',
        },
      },
    ],
    [formatMessage]
  );

  if (!userLoaded && !sessionLoaded) {
    return (<ScreenLoader />)
  }

  if (user == undefined || user == null || session == undefined || session == null) {
    return (<ScreenLoader />)
  }

  const fetchMembership = async (params: TDataGridRequestParams) => {
    try {

      if (!user?.id) {
        toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
        return {
          data: [],
          totalCount: 0
        };
      }

      if (!session?.id) {
        toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
        return {
          data: [],
          totalCount: 0
        };
      }

      let _res = await fetchUserMembershis(user?.id, session?.id);

      if (_res?.data?.error || _res?.error) {
        toast.error(_res?.data?.error ? _res?.data?.error : _res?.error  || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
      } else {
        return {
          data: _res?.data?.data,
          totalCount: _res?.data?.data?.length ?? 0
        };
      }
    } catch (error) {
      toast(formatMessage({ id: 'FAILED_TO_FETCH_MEMBERSHIP_LIST_DATA' }));

      return {
        data: [],
        totalCount: 0
      };
    }

  };
  const Listbar = ({ }: any) => {

    return (
      <div className="card-header border-b-0 px-5">
        <h3 className="card-title"><FormattedMessage id="MEMBERSHIP_LIST" /></h3>
      </div>
    );
  };

  return (
    <Fragment>

      <DataGrid
        columns={columns}
        serverSide={true}
        onFetchData={fetchMembership}
        rowSelection={true}
        getRowId={(row: any) => row.id}
        pagination={{ size: total }}
        layout={{
          card: true,
          classes: {
            container: 'd-none-data-pagination'
          }
        }}
        toolbar={<Listbar />}
      />
      {viewModalOpen && <MembershipView open={viewModalOpen} onOpenChange={handleViewModalClose} details={details} />}
    </Fragment>
  );
};

export default MembershipList;
